import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, Barcode, Lock, Unlock, ChevronRight, Send } from "lucide-react";

const STATUS_STYLE = {
  active: "bg-emerald-100 text-emerald-800",
  frozen: "bg-blue-100 text-blue-800",
  draft: "bg-zinc-100 text-zinc-700",
};

export default function SKUManagement() {
  const [skus, setSKUs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ style_name: "", gender: "", product_category: "", colorways: "", sizes: "", hs_code: "", company_prefix: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/sku-records")
      .then((r) => setSKUs(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true);
    try {
      const colorways = form.colorways.split(",").map((s) => s.trim()).filter(Boolean);
      const sizes = form.sizes.split(",").map((s) => s.trim()).filter(Boolean);
      const variant_matrix = [];
      for (const cw of colorways) {
        for (const sz of sizes) {
          variant_matrix.push({ colorway: cw, size: sz });
        }
      }
      await api.post("/sku-records", {
        style_code: form.style_name,
        gender: form.gender,
        product_category: form.product_category,
        hs_code: form.hs_code,
        company_prefix: form.company_prefix,
        variant_matrix,
      });
      toast.success("SKU record created");
      setShowCreate(false);
      setForm({ style_name: "", gender: "", product_category: "", colorways: "", sizes: "", hs_code: "", company_prefix: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleFreeze = async (id, currentStatus) => {
    const next = currentStatus === "frozen" ? "active" : "frozen";
    try {
      await api.patch(`/sku-records/${id}/status`, { status: next });
      toast.success(`SKU ${next === "frozen" ? "frozen" : "activated"}`);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const loadDetail = (sku) => {
    api.get(`/sku-records/${sku.id}`).then((r) => setSelected(r.data)).catch(() => setSelected(sku));
  };

  const erpHandover = async (id) => {
    try {
      await api.post(`/sku-records/${id}/erp-handover`);
      toast.success("ERP handover initiated");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">PLM</p>
          <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">SKU / Item Master</h1>
          <p className="text-zinc-500 mt-2 text-sm">Manage SKU records, variant matrices and barcode generation.</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">
          <Plus size={15} /> New SKU record
        </button>
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-zinc-400">Loading...</div>
      ) : skus.length === 0 ? (
        <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
          <Barcode size={26} className="mx-auto mb-3" />
          <p className="text-sm">No SKU records yet.</p>
        </div>
      ) : (
        <div className="mt-8 flex gap-6">
          {/* SKU List */}
          <div className="flex-1 border border-zinc-200 bg-white rounded-lg overflow-hidden">
            <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 text-[11px] uppercase tracking-[0.15em] text-zinc-400">
              <span className="col-span-4">Style</span>
              <span className="col-span-2">Variants</span>
              <span className="col-span-3">Category</span>
              <span className="col-span-2">Status</span>
              <span className="col-span-1 text-right"></span>
            </div>
            {skus.map((s) => (
              <div key={s.id} className={`grid grid-cols-12 items-center px-6 py-4 border-b border-zinc-50 cursor-pointer hover:bg-zinc-50 ${selected?.id === s.id ? "bg-zinc-50" : ""}`}
                onClick={() => loadDetail(s)}>
                <span className="col-span-4 text-sm font-medium">{s.style_code || "-"}</span>
                <span className="col-span-2 text-sm text-zinc-600">{(s.variant_matrix || []).length}</span>
                <span className="col-span-3 text-sm text-zinc-600">{s.gender || "-"} / {s.product_category || "-"}</span>
                <span className="col-span-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[s.status] || STATUS_STYLE.draft}`}>{s.status}</span>
                </span>
                <span className="col-span-1 text-right"><ChevronRight size={14} className="inline text-zinc-400" /></span>
              </div>
            ))}
          </div>

          {/* Detail Panel with Variant Matrix */}
          {selected && (() => {
            const matrix = selected.variant_matrix || [];
            const colorways = [...new Set(matrix.map((v) => v.colorway || v.colourway).filter(Boolean))];
            const sizes = [...new Set(matrix.map((v) => v.size).filter(Boolean))];
            return (
              <div className="w-96 shrink-0 border border-zinc-200 bg-white rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading font-semibold">{selected.style_code || selected.style_name}</h3>
                  <button onClick={() => toggleFreeze(selected.id, selected.status)}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium ${selected.status === "frozen" ? "bg-emerald-50 text-emerald-800" : "bg-blue-50 text-blue-800"}`}>
                    {selected.status === "frozen" ? <><Unlock size={12} /> Activate</> : <><Lock size={12} /> Freeze</>}
                  </button>
                </div>

                {/* Meta */}
                <div className="mt-3 space-y-1 text-xs text-zinc-500">
                  {selected.gender && <div>Gender: <span className="font-medium text-zinc-700">{selected.gender}</span></div>}
                  {selected.product_category && <div>Category: <span className="font-medium text-zinc-700">{selected.product_category}</span></div>}
                  {selected.hs_code && <div>HS Code: <span className="font-mono font-medium text-zinc-700">{selected.hs_code}</span></div>}
                  {selected.company_prefix && <div>Company Prefix: <span className="font-mono font-medium text-zinc-700">{selected.company_prefix}</span></div>}
                </div>

                {/* Variant Matrix (Size x Colorway) */}
                {matrix.length > 0 && (
                  <div className="mt-4">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-2">Variant Matrix ({matrix.length} variants)</p>
                    {colorways.length > 0 && sizes.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="text-xs w-full border-collapse">
                          <thead>
                            <tr>
                              <th className="border border-zinc-200 px-2 py-1.5 bg-zinc-50 text-left text-zinc-400">Size \ Color</th>
                              {colorways.map((c) => (
                                <th key={c} className="border border-zinc-200 px-2 py-1.5 bg-zinc-50 text-center font-medium">{c}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {sizes.map((sz) => (
                              <tr key={sz}>
                                <td className="border border-zinc-200 px-2 py-1.5 bg-zinc-50 font-medium">{sz}</td>
                                {colorways.map((c) => {
                                  const variant = matrix.find((v) => v.size === sz && (v.colorway === c || v.colourway === c));
                                  return (
                                    <td key={`${sz}-${c}`} className="border border-zinc-200 px-2 py-1.5 text-center">
                                      {variant ? (
                                        <span className="font-mono text-[10px]">{variant.ean13 || variant.sku_code || "---"}</span>
                                      ) : (
                                        <span className="text-zinc-300">-</span>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {matrix.slice(0, 10).map((v, i) => (
                          <div key={i} className="text-xs bg-zinc-50 rounded px-2 py-1">
                            {v.colorway || v.colourway || ""} / {v.size || ""} {v.sku_code ? `- ${v.sku_code}` : ""}
                          </div>
                        ))}
                        {matrix.length > 10 && <p className="text-xs text-zinc-400">...and {matrix.length - 10} more</p>}
                      </div>
                    )}
                  </div>
                )}

                {/* ERP Handover */}
                {selected.erp_handover_package ? (
                  <div className="mt-4 pt-4 border-t border-zinc-100">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-600 mb-1">ERP Handover Complete</p>
                    <p className="text-xs text-zinc-500">Package generated and SKU frozen.</p>
                  </div>
                ) : (
                  <div className="mt-4 pt-4 border-t border-zinc-100">
                    <button onClick={() => erpHandover(selected.id)}
                      disabled={selected.status === "frozen"}
                      className="w-full inline-flex items-center justify-center gap-1.5 bg-zinc-950 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                      <Send size={14} /> ERP Handover
                    </button>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">New SKU record</h2>
              <button onClick={() => setShowCreate(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input value={form.style_name} onChange={(e) => setForm({ ...form, style_name: e.target.value })} placeholder="Style code"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <div className="grid grid-cols-2 gap-3">
                <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                  <option value="">Gender</option>
                  <option value="men">Men</option>
                  <option value="women">Women</option>
                  <option value="unisex">Unisex</option>
                  <option value="kids">Kids</option>
                </select>
                <input value={form.product_category} onChange={(e) => setForm({ ...form, product_category: e.target.value })} placeholder="Category (e.g. Tops)"
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              </div>
              <input value={form.colorways} onChange={(e) => setForm({ ...form, colorways: e.target.value })} placeholder="Colorways (comma separated, e.g. Black, Navy, White)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} placeholder="Sizes (comma separated, e.g. XS, S, M, L, XL)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <div className="grid grid-cols-2 gap-3">
                <input value={form.hs_code} onChange={(e) => setForm({ ...form, hs_code: e.target.value })} placeholder="HS Code (optional)"
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                <input value={form.company_prefix} onChange={(e) => setForm({ ...form, company_prefix: e.target.value })} placeholder="GS1 prefix (optional)"
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              </div>
              <button onClick={create} disabled={!form.style_name || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Creating..." : "Create SKU record"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
